<template>
    <Modal
        v-model="showModal"
        draggable
        sticky
        title="Edit Project Timelog"
        width="600"
        no-footer
        class="editProjectLogModal"
    >
        <Form
            ref="form"
            :model="form"
            :rules="ruleInline"
            :loading="isLoading"
            label-position="left"
            class="edit-project-log-wrapper"
        >
            <Row>
                <Col span="12">
                    <FormItem label="Project Name">
                        <Input v-model="projectName" disabled />
                    </FormItem>
                </Col>
            </Row>
            <Row :gutter="16">
                <Col span="12">
                    <FormItem label="Start Time" prop="startTime">
                        <TimePicker
                            v-model="form.startTime"
                            type="time"
                            placeholder="Select start time"
                            format="HH:mm"
                            confirm
                            transfer
                            style="width: 100%"
                            :disabled-hours="disabledStartHours"
                            :disabled-minutes="disabledStartMinutes"
                            @on-change="updateDisabledStartMinutes"
                        />
                    </FormItem>
                </Col>
                <Col span="12">
                    <FormItem label="End Time" prop="endTime">
                        <TimePicker
                            v-model="form.endTime"
                            type="time"
                            placeholder="Select end time"
                            format="HH:mm"
                            confirm
                            transfer
                            style="width: 100%"
                            :disabled-hours="disabledEndHours"
                            :disabled-minutes="disabledEndMinutes"
                            @on-change="updateDisabledEndMinutes"
                        />
                    </FormItem>
                </Col>
            </Row>
            <Row>
                <Col span="24">
                    <FormItem label="Reason for dispute" prop="reason">
                        <Input
                            v-model="form.reason"
                            type="textarea"
                            :autosize="{ minRows: 2, maxRows: 5 }"
                            placeholder="Enter reason..."
                        />
                    </FormItem>
                </Col>
            </Row>
        </Form>
        <div slot="footer" style="text-align: right; margin-top: 20px">
            <Button class="pt-btn-2" style="margin-right: 10px" @click="showModal = false">CANCEL</Button>
            <Button class="pt-btn-3" :loading="isLoading" @click="handleSubmit">SUBMIT</Button>
        </div>
    </Modal>
</template>

<script>
import moment from 'moment';
import { submitRequestV2 } from '@/services/requests.services';

export default {
    data() {
        return {
            showModal: false,
            isLoading: false,
            projectName: '',
            timelog: null,
            prevTimelog: null,
            nextTimelog: null,
            form: {
                startTime: '',
                endTime: '',
                reason: '',
            },
            ruleInline: {
                startTime: [{ required: true, message: 'Please select start time', trigger: 'change' }],
                endTime: [{ required: true, message: 'Please select end time', trigger: 'change' }],
                reason: [{ required: true, message: 'Please enter reason', trigger: 'blur' }],
            },
            disabledStartHours: [],
            disabledStartMinutes: [],
            disabledEndHours: [],
            disabledEndMinutes: [],
        };
    },
    methods: {
        openModal(timelog, prevTimelog, nextTimelog) {
            console.log('timelog: ', timelog);
            console.log('prevTimelog: ', prevTimelog);
            console.log('nextTimelog: ', nextTimelog);
            this.timelog = timelog;
            this.prevTimelog = prevTimelog;
            this.nextTimelog = nextTimelog;
            this.projectName = timelog.project ? timelog.project.name : '-';

            this.form.startTime = moment(timelog.startTime).utc().format('HH:mm');
            this.form.endTime = moment(timelog.endTime).utc().format('HH:mm');
            this.form.reason = '';

            this.disabledStartHours = this._disabledStartHours();
            this.disabledEndHours = this._disabledEndHours();

            // Initial disabled minutes based on current values
            this.updateDisabledStartMinutes(this.form.startTime);
            this.updateDisabledEndMinutes(this.form.endTime);

            this.showModal = true;
        },
        updateDisabledStartMinutes(startTime) {
            if (startTime) {
                const hour = parseInt(startTime.split(':')[0]);
                this.disabledStartMinutes = this._disabledStartMinutes(hour);
            } else {
                this.disabledStartMinutes = [];
            }
        },
        updateDisabledEndMinutes(endTime) {
            if (endTime) {
                const hour = parseInt(endTime.split(':')[0]);
                this.disabledEndMinutes = this._disabledEndMinutes(hour);
            } else {
                this.disabledEndMinutes = [];
            }
        },
        _disabledStartHours() {
            let hours = [];
            if (this.prevTimelog && this.prevTimelog.endTime) {
                const prevEnd = moment(this.prevTimelog.endTime).utc();
                for (let i = 0; i < prevEnd.hour(); i++) {
                    hours.push(i);
                }
            }
            return hours;
        },
        _disabledStartMinutes(hour) {
            let minutes = [];
            if (this.prevTimelog && this.prevTimelog.endTime) {
                const prevEnd = moment(this.prevTimelog.endTime).utc();
                if (hour === prevEnd.hour()) {
                    for (let i = 0; i < prevEnd.minute(); i++) {
                        minutes.push(i);
                    }
                }
            }
            return minutes;
        },
        _disabledEndHours() {
            let hours = [];
            if (this.nextTimelog && this.nextTimelog.startTime) {
                const nextStart = moment(this.nextTimelog.startTime).utc();
                for (let i = nextStart.hour() + 1; i < 24; i++) {
                    hours.push(i);
                }
            }
            return hours;
        },
        _disabledEndMinutes(hour) {
            let minutes = [];
            if (this.nextTimelog && this.nextTimelog.startTime) {
                const nextStart = moment(this.nextTimelog.startTime).utc();
                if (hour === nextStart.hour()) {
                    for (let i = nextStart.minute() + 1; i < 60; i++) {
                        minutes.push(i);
                    }
                }
            }
            return minutes;
        },
        handleSubmit() {
            this.$refs.form.validate((valid) => {
                if (valid) {
                    this.submitDispute();
                }
            });
        },
        async submitDispute() {
            this.isLoading = true;
            try {
                const dateStr = moment(this.timelog.startTime).utc().format('YYYY-MM-DD');
                const requestedStart = moment.utc(`${dateStr} ${this.form.startTime}`, 'YYYY-MM-DD HH:mm').format();
                const requestedEnd = moment.utc(`${dateStr} ${this.form.endTime}`, 'YYYY-MM-DD HH:mm').format();

                const data = {
                    requestedStart,
                    requestedEnd,
                    timelog: this.timelog.id,
                    type: 'projectlogdispute',
                    reason: this.form.reason,
                };

                await submitRequestV2(data);
                this.$Message.success('Project log dispute request submitted successfully');
                this.showModal = false;
                this.$emit('success');
            } catch (error) {
                console.error(error);
                this.$Message.error(error.data?.message || 'Failed to submit project log dispute request');
            } finally {
                this.isLoading = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.editProjectLogModal {
    .edit-project-log-wrapper {
        padding: 10px;
    }
}
</style>
